import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Box,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

import LogisticCategoryApi from "Services/LogisticCategory";
import servicetypeapi from "Services/Servicetype";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LogisticCategoryUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [imagePreview, setImagePreview] = useState(null);
  const [serviceList, setServiceList] = useState([]);
  const [initialData, setInitialData] = useState({
    serviceTypeId: "",
    subservicetypeid: "",
    serviceCategory: "",
    heigh: "",
    width: "",
    length: "",
    capacityinkg: "",
    image: null,
  });

  useEffect(() => {
    if (openDialog) {
      servicetypeapi
        .getServicesList({ limit: "unlimited" })
        .then((res) => {
          const list = res?.data?.data?.data || res?.data?.data || [];
          setServiceList(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          console.error("Failed to fetch service types", err);
          setServiceList([]);
        });
    }
  }, [openDialog]);

  const fetchCategoryDetails = useCallback(() => {
    if (!id) return;
    dispatch(showLoader("Fetching details..."));
    LogisticCategoryApi.getLogisticCategoryById(id)
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true && res?.data?.data) {
          const cat = res?.data?.data;
          let selectedServiceTypeId = "";
          if (cat.serviceTypeId && typeof cat.serviceTypeId === "object") {
            selectedServiceTypeId = cat.serviceTypeId._id || "";
          } else if (cat.serviceTypeId) {
            selectedServiceTypeId = cat.serviceTypeId;
          }

          let selectedSubServiceTypeId = "";
          if (cat.subservicetypeid && typeof cat.subservicetypeid === "object") {
            selectedSubServiceTypeId = cat.subservicetypeid._id || "";
          } else if (cat.subservicetypeid) {
            selectedSubServiceTypeId = cat.subservicetypeid;
          }

          setInitialData({
            serviceTypeId: selectedServiceTypeId,
            subservicetypeid: selectedSubServiceTypeId,
            serviceCategory: cat.serviceCategory || "",
            heigh: cat.heigh || "",
            width: cat.width || "",
            length: cat.length || "",
            capacityinkg: cat.capacityinkg || "",
            image: null,
          });
          setImagePreview(cat.image || null);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch details",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      });
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog && id) {
      fetchCategoryDetails();
    }
  }, [openDialog, id, fetchCategoryDetails]);

  const maxFileSize = 5242880;

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("image", file);
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleUpdateCategory = (formValues) => {
    dispatch(showLoader("Updating please wait..."));

    const formData = new FormData();
    formData.append("serviceTypeId", formValues.serviceTypeId);
    formData.append("subservicetypeid", formValues.subservicetypeid || "");
    formData.append("serviceCategory", formValues.serviceCategory.trim());
    if (formValues.heigh !== undefined) {
      formData.append("heigh", (formValues.heigh || "").trim());
    }
    if (formValues.width !== undefined) {
      formData.append("width", (formValues.width || "").trim());
    }
    if (formValues.length !== undefined) {
      formData.append("length", (formValues.length || "").trim());
    }
    if (formValues.capacityinkg !== undefined) {
      formData.append("capacityinkg", (formValues.capacityinkg || "").trim());
    }
    if (formValues.image) {
      formData.append("image", formValues.image);
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Logistic Category Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        handleModalClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error updating Logistic Category",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    LogisticCategoryApi.updateLogisticCategory(id, formData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: yup.object({
      serviceTypeId: yup.string().required("Service Type is required"),
      subservicetypeid: yup.string().nullable(),
      serviceCategory: yup.string().required("Service Category is required"),
      heigh: yup.string(),
      width: yup.string(),
      length: yup.string(),
      capacityinkg: yup.string(),
    }),
    onSubmit: handleUpdateCategory,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setImagePreview(null);
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.headerBar}>
        <Typography className={classes.headerTitle}>Edit Logistic Category</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeIconBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.contentContainer}>
        <Typography className={classes.fieldLabel}>Category Image</Typography>
        <Box {...getRootProps()} onClick={open} className={classes.uploadBox}>
          <input {...getInputProps()} />
          {imagePreview ? (
            <Box component="img" src={imagePreview} alt="Preview" className={classes.previewImage} />
          ) : (
            <>
              <FileUploadOutlinedIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadText}>Click to change image</Typography>
            </>
          )}
        </Box>

        <Typography className={classes.fieldLabel}>Service Type</Typography>
        <TextField
          select
          name="serviceTypeId"
          id="serviceTypeId"
          value={formik.values.serviceTypeId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors.serviceTypeId && formik.touched.serviceTypeId)}
          helperText={formik.touched.serviceTypeId && formik.errors.serviceTypeId}
          fullWidth
          variant="outlined"
          className={classes.textField}
          SelectProps={{
            displayEmpty: true,
          }}
        >
          <MenuItem value="" disabled>
            Select Service Type
          </MenuItem>
          {serviceList.map((service) => (
            <MenuItem key={service._id} value={service._id}>
              {service.serviceName}
            </MenuItem>
          ))}
        </TextField>

        <Typography className={classes.fieldLabel}>Sub Service Type</Typography>
        <TextField
          select
          name="subservicetypeid"
          id="subservicetypeid"
          value={formik.values.subservicetypeid}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors.subservicetypeid && formik.touched.subservicetypeid)}
          helperText={formik.touched.subservicetypeid && formik.errors.subservicetypeid}
          fullWidth
          variant="outlined"
          className={classes.textField}
          SelectProps={{
            displayEmpty: true,
          }}
        >
          <MenuItem value="">
            <em>Select Sub Service Type (Optional)</em>
          </MenuItem>
          {serviceList.map((service) => (
            <MenuItem key={service._id} value={service._id}>
              {service.serviceName}
            </MenuItem>
          ))}
        </TextField>

        <Typography className={classes.fieldLabel}>Service Category</Typography>
        <TextField
          name="serviceCategory"
          id="serviceCategory"
          placeholder="e.g. Parcel, Freight, Cold Storage"
          value={formik.values.serviceCategory}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors.serviceCategory && formik.touched.serviceCategory)}
          helperText={formik.touched.serviceCategory && formik.errors.serviceCategory}
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.fieldLabel}>Height</Typography>
            <TextField
              name="heigh"
              id="heigh"
              placeholder="e.g. 6 ft"
              value={formik.values.heigh}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.heigh && formik.touched.heigh)}
              helperText={formik.touched.heigh && formik.errors.heigh}
              fullWidth
              variant="outlined"
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.fieldLabel}>Width</Typography>
            <TextField
              name="width"
              id="width"
              placeholder="e.g. 5 ft"
              value={formik.values.width}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.width && formik.touched.width)}
              helperText={formik.touched.width && formik.errors.width}
              fullWidth
              variant="outlined"
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.fieldLabel}>Length</Typography>
            <TextField
              name="length"
              id="length"
              placeholder="e.g. 10 ft"
              value={formik.values.length}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.length && formik.touched.length)}
              helperText={formik.touched.length && formik.errors.length}
              fullWidth
              variant="outlined"
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.fieldLabel}>Capacity (in KG)</Typography>
            <TextField
              name="capacityinkg"
              id="capacityinkg"
              placeholder="e.g. 1000"
              value={formik.values.capacityinkg}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.capacityinkg && formik.touched.capacityinkg)}
              helperText={formik.touched.capacityinkg && formik.errors.capacityinkg}
              fullWidth
              variant="outlined"
              className={classes.textField}
            />
          </Grid>
        </Grid>

        <Box className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleModalClose} className={classes.cancelBtn}>
            Close
          </Button>
          <Button variant="contained" onClick={formik.handleSubmit} className={classes.submitBtn}>
            Update Category
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LogisticCategoryUpdate;
